import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
//old import { CreditCard, Calendar, Crown, Clock, XCircle, FileText, Shield, ScrollText, ArrowUpRight } from 'lucide-react';
import { CreditCard, Calendar, Crown, Clock, XCircle, FileText, Shield, ArrowUpRight } from 'lucide-react';
//old import { useNavigate } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import CoachMembershipSkeleton from '../components/layout/CoachMembershipSkeleton';

export default function MyMembership() {
    const { isDarkMode } = useTheme();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [membership, setMembership] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [undoing, setUndoing] = useState(false);
    // Manage billing: Stripe's page for the card and the receipts
    const [openingBilling, setOpeningBilling] = useState(false);
    const [billingError, setBillingError] = useState('');
    // What a plan change on the plans page did (it comes back here to say so), or a cancel that ended the plan now
    const [notice, setNotice] = useState(() => location.state?.notice || null);

    useEffect(() => {
        // Wait until auth has settled and a user is confirmed before hitting
        // /membership/me — fetching during the post-login auth flap causes a
        // remount/refetch storm.
        if (authLoading) return;
        if (!user) { setLoading(false); return; }
        const fetchMembership = async () => {
            try {
                const response = await api.get('/membership/me');
                setMembership(response.data);
            } catch (err) {
                console.error("No membership found or error fetching", err);
            } finally {
                setLoading(false);
            }
        };
        fetchMembership();
    }, [authLoading, user]);

    // The notice shows once: a refresh doesn't bring it back
    useEffect(() => {
        if (location.state?.notice) navigate(location.pathname, { replace: true });
    }, [location, navigate]);

    if (loading) {
    //old return (
    //old <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
    //old <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
    //old <span className={`ml-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading membership details...</span>
    //old </div>
    //old );
    // The same page layout as the coach's My Membership
    return <CoachMembershipSkeleton isDarkMode={isDarkMode} />;
  }

    const handleManageSubscription = async () => {
        setSubscribing(true);
        try {
            const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || "price_12345"; 
            const email = membership?.email || membership?.user_email || "";
            const response = await api.post('/membership/create-checkout-session', {
                price_id: priceId,
                customer_email: email,
            });
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (err) {
            alert("Could not start subscription process. Please try again.");
        } finally {
            setSubscribing(false);
        }
    };

    const handleCancelMembership = async () => {
        setCancelling(true);
        try {
            await api.post('/membership/cancel');
            //old setMembership(prev => prev ? { ...prev, CancelledAt: new Date().toISOString() } : prev);
            // Read back: Cancel also picks the free plan for when the paid period ends, and Keep is offered
            const response = await api.get('/membership/me');
            // A renewal the card didn't pay ends now instead (Stripe stops retrying it): no Cancelled banner, so say so
            if (membership?.StripeSubscriptionStatus === 'past_due' && !response.data?.CancelledAt) {
                setNotice("Cancelled: you're on the free plan now. The payment that didn't go through isn't tried again.");
            }
            setMembership(response.data);
            setShowCancelConfirm(false);
        } catch (err) {
            alert('Failed to cancel membership. Please try again.');
        } finally {
            setCancelling(false);
        }
    };

    // Undo a scheduled downgrade — stay on the current paid plan (no charge).
    const handleCancelDowngrade = async () => {
        setUndoing(true);
        try {
            await api.post('/membership/cancel-downgrade');
            const response = await api.get('/membership/me');
            setMembership(response.data);
        } catch (err) {
            alert(err.response?.data?.error || 'Could not keep your current plan. Please try again.');
        } finally {
            setUndoing(false);
        }
    };

    // Manage billing: change the card or see receipts in Stripe's portal (cancelling stays here)
    const handleManageBilling = async () => {
        setOpeningBilling(true);
        setBillingError('');
        try {
            const res = await api.post('/membership/billing');
            window.location.href = res.data.url;
        } catch (err) {
            setBillingError(err.response?.data?.error || 'Could not open billing. Please try again.');
            setOpeningBilling(false);
        }
    };

    // Whether and when the card is charged (Evan, Jul 20): a free plan has no date, a plan
    // given at no charge (no card behind it) has no price, no renewal and no Cancel. Not how
    // much: a promo code's discount stays on the subscription and Stripe charges less
    const covered = membership?.CoveredBy;
    const paidByCard = membership?.isActive && membership?.HasSubscription;
    const isFree = Number(membership?.Price || 0) === 0 && !membership?.TrialDays;
    const pendingPaid = membership?.PendingTierID && Number(membership?.PendingPrice) > 0;
    // A renewal the card didn't pay (past_due): Stripe tries again, with the new card once it's changed
    const pastDue = paidByCard && membership?.StripeSubscriptionStatus === 'past_due';
    const endDate = membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : 'Ongoing';
    const [dateLabel, dateValue] = covered ? ['Paid by', covered.OrgName]
        : isFree ? ['Price', 'Free']
        : !paidByCard || membership?.CancelledAt || (membership?.PendingTierID && !pendingPaid) ? ['Ends', endDate]
        : pendingPaid ? ['Switches', endDate]
        : pastDue ? ['Payment due', endDate]
        : membership?.IsTrialing ? ['First charge', endDate]
        : ['Renews', endDate];
    const statusLine = covered
        ? `${covered.OrgName} covers ${covered.TierName} while your license is active. You don't pay for it.`
        : isFree || !membership?.isActive || membership?.PendingTierID || membership?.CancelledAt ? null
        : !paidByCard ? `Given at no charge: nothing is charged. When it ends you're on the free plan.`
        : pastDue ? `Your last payment didn't go through. Update your card with Manage billing and we'll try again.`
        : membership?.IsTrialing ? `Your free trial ends on ${endDate}. Your first charge is that day.`
        : `Your next charge is on ${endDate}.`;

    return (
        <div className={`max-w-2xl mx-auto space-y-6 py-10 px-4 ${isDarkMode ? 'bg-surface' : ''}`}>
            {/* Header */}
            <header className="text-center">
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>My Membership</h1>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Manage your subscription and billing</p>
            </header>

            {notice && (
                <div role="status" className={`rounded-xl border p-3.5 text-sm font-medium ${isDarkMode ? 'bg-primary-900/30 text-primary-300 border-primary-800/50' : 'bg-primary-50 text-primary-800 border-primary-200'}`}>
                    {notice}
                </div>
            )}

            {/* Scheduled-downgrade banner — the member chose a lower tier but keeps
                their paid features until EndDate, and can undo it for free. */}
            {/* old {membership?.PendingTierID && ( */}
            {/* Cancel picks the free plan for then too (one way out); a plan cancelled before that has only CancelledAt */}
            {(membership?.PendingTierID || membership?.CancelledAt) && (
                <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${isDarkMode ? 'bg-slate-800 border-warning/40' : 'bg-warning-tint border-warning/40'}`}>
                    <div className="flex items-start gap-3 flex-1">
                        <Clock className="text-warning shrink-0 mt-0.5" size={18} />
                        <div>
                            <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                {/* old Switching to {membership.PendingTierName || 'Free'} on {membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : 'your renewal date'} */}
                                {paidByCard && !pendingPaid
                                    ? `Cancelled: ${membership.TierName} ends on ${endDate}`
                                    : `Switching to ${membership.PendingTierName || 'Free'} on ${membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : 'your renewal date'}`}
                            </p>
                            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                {/* old You keep your {membership.TierName} features until then. Switch back anytime before that — no charge. */}
                                {pendingPaid
                                    ? `You keep ${membership.TierName} until then. Switch back anytime before that — no charge.`
                                    : paidByCard
                                    ? `Then you're on ${membership.PendingTierName || 'the free plan'} and your card isn't charged again. Keep ${membership.TierName} anytime before that.`
                                    : `You keep your ${membership.TierName} features until then. Switch back anytime before that — no charge.`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleCancelDowngrade}
                        disabled={undoing}
                        className="whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50"
                    >
                        {undoing ? 'Working…' : `Keep ${membership.TierName}`}
                    </button>
                </div>
            )}

            {/* Plan Card */}
            <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
                {/* Plan Banner */}
                <div className="bg-slate-900 px-6 py-6 text-center relative">
                    <Crown className="text-amber-400 mx-auto mb-2" size={28} />
                    {/* old <h2 className="text-xl font-bold text-white">{membership?.TierName || "Free Tier"}</h2> */}
                    {/* A licensed student has the organization's plan (Evan, Sep 28) */}
                    <h2 className="text-xl font-bold text-white">{covered ? covered.TierName : (membership?.TierName || "Free Tier")}</h2>
                    {covered && <p className="mt-1 text-sm text-slate-300">Covered by {covered.OrgName}</p>}
                    <span className={`inline-block mt-2 px-3 py-0.5 rounded-full text-xs font-bold border ${
                        membership?.CancelledAt
                            ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                            : membership?.isActive 
                                ? 'bg-primary-500/20 text-primary-400 border-primary-500/30' 
                                : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                        {/* old {membership?.CancelledAt ? 'Cancelled' : membership?.isActive ? 'Active' : 'Inactive'} */}
                        {membership?.CancelledAt ? 'Cancelled' : membership?.IsTrialing ? 'Free trial' : membership?.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {/* {membership?.Price && ( */}
                    {/* A $0 plan's Price is the number 0, which React prints as "0" */}
                    {/* old {Number(membership?.Price) > 0 && ( */}
                    {/* Only a plan the card pays for has a price here (one given at no charge has none) */}
                    {Number(membership?.Price) > 0 && membership?.HasSubscription && (
                        <p className="mt-3">
                            <span className="text-3xl font-extrabold text-primary-400">${Number(membership.Price).toFixed(2)}</span>
                            {/* <span className="text-sm text-slate-400">/mo</span> */}
                            <span className="text-sm text-slate-400">{membership.BillingFrequency === 'yearly' ? '/yr' : '/mo'}</span>
                        </p>
                    )}
                </div>

                {/* Details */}
                <div className="px-6 py-5 space-y-5">
                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Calendar className="text-primary-500" size={14} />
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>Started</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>
                                {membership?.StartDate ? new Date(membership.StartDate).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                        <div className={`rounded-xl p-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="text-primary-500" size={14} />
                                {/* old <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{(membership?.CancelledAt || membership?.PendingTierID) ? 'Ends' : 'Renews'}</p> */}
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{dateLabel}</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>
                                {/* old {membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : 'Ongoing'} */}
                                {dateValue}
                            </p>
                        </div>
                    </div>
                    {/* (a payment that didn't go through reads like the page's other errors) */}
                    {statusLine && <p className={pastDue ? 'text-sm font-medium text-red-500' : `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{statusLine}</p>}

                    {/* Actions */}
                    <div className="space-y-2">
                        <button
                            className="w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600 shadow-sm"
                            onClick={() => navigate('/membership/plans')}
                            disabled={subscribing}
                        >
                            <ArrowUpRight size={16} /> {membership?.isActive ? 'View Plans' : 'Upgrade Now'}
                        </button>
                        {/* The card behind a plan bought with Stripe: change it, see receipts */}
                        {paidByCard && (
                            <button
                                onClick={handleManageBilling}
                                disabled={openingBilling}
                                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border disabled:opacity-50 ${
                                    isDarkMode
                                        ? 'text-slate-200 border-slate-600 hover:bg-slate-700'
                                        : 'text-slate-700 border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                <CreditCard size={16} /> {openingBilling ? 'Opening…' : 'Manage billing'}
                            </button>
                        )}
                        {billingError && <p className="text-sm font-medium text-red-500">{billingError}</p>}
                        {/* old {membership?.isActive && !membership?.CancelledAt && !membership?.PendingTierID && Number(membership?.Price) > 0 && ( */}
                        {/* Only a plan the card pays for (one given at no charge just ends); a cheaper paid plan waiting can still be cancelled */}
                        {paidByCard && !membership?.CancelledAt && (!membership?.PendingTierID || pendingPaid) && Number(membership?.Price) > 0 && (
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border ${
                                    isDarkMode 
                                        ? 'text-red-400 border-red-800/50 hover:bg-red-900/20' 
                                        : 'text-red-500 border-red-200 hover:bg-red-50'
                                }`}
                            >
                                <XCircle size={16} /> Cancel Membership
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Legal Links */}
            <div className={`flex flex-wrap justify-center gap-5 text-xs ${
                isDarkMode ? 'text-slate-500' : 'text-slate-400'
            }`}>
                {/* The app has no pages of its own for these, so they open the official site like the landing page's links; the site has no separate membership agreement, so it opens the Terms of Service */}
                {/* old <a href="/membership-agreement" className="flex items-center gap-1 hover:text-slate-600 transition-colors"> */}
                {/* Terms of Service and Privacy Policy only, until there's a membership agreement of its own (it opened the Terms of Service) */}
                {/* old <a href="https://buildmybizbrand.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-600 transition-colors"> */}
                {/* old <ScrollText size={12} /> Membership Agreement */}
                {/* old </a> */}
                {/* old <a href="/terms-of-service" className="flex items-center gap-1 hover:text-slate-600 transition-colors"> */}
                <a href="https://ponopilot.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-600 transition-colors">
                    <FileText size={12} /> Terms of Service
                </a>
                {/* old <a href="/privacy-policy" className="flex items-center gap-1 hover:text-slate-600 transition-colors"> */}
                <a href="https://ponopilot.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-600 transition-colors">
                    <Shield size={12} /> Privacy Policy
                </a>
            </div>

            {/* Cancel Confirmation Modal */}
            {showCancelConfirm && createPortal(
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 9999 }} onClick={() => setShowCancelConfirm(false)}>
                    <div className={`rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full"><XCircle className="text-red-500" size={24} /></div>
                            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Cancel Membership</h3>
                        </div>
                        <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {/* old Are you sure you want to cancel your membership? Your subscription will remain active until the end of your current billing period. */}
                            {/* The day it ends and that nothing more is charged (Evan, Sep 9); a renewal the card didn't pay ends now, as Stripe then stops retrying it */}
                            {pastDue
                                ? `Your last payment didn't go through, so if you cancel, ${membership?.TierName} ends now and you're on the free plan. That payment isn't tried again and nothing more is charged.`
                                : `You keep ${membership?.TierName} until ${endDate}. Then you're on the free plan and your card isn't charged again. You can keep ${membership?.TierName} anytime before then.`}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className={`flex-1 py-3 rounded-xl font-semibold border transition-colors ${
                                    isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                Keep Membership
                            </button>
                            <button
                                onClick={handleCancelMembership}
                                disabled={cancelling}
                                className="flex-1 py-3 rounded-xl font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}